import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Calendar, MapPin, Wine, CheckCircle2, Send } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { JuenWordmark } from "@/components/juen-wordmark";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

export const Route = createFileRoute("/vouchers/$voucherId")({
  head: () => ({ meta: [{ title: "İçecek Kuponu — JUËN" }] }),
  component: VoucherDetailPage,
});

interface VoucherDetail {
  id: string;
  code: string;
  created_at: string;
  redeemed_at: string | null;
  events: { id: string; name: string; starts_at: string; venue: string | null } | null;
  drinks: { name: string; description: string | null } | null;
}

function VoucherDetailPage() {
  const { voucherId } = Route.useParams();
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [voucher, setVoucher] = useState<VoucherDetail | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferring, setTransferring] = useState(false);
  const tc = useAutoTranslate([voucher?.drinks?.name, voucher?.events?.name, voucher?.events?.venue]);

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const { data } = await supabase
        .from("drink_vouchers")
        .select("id,code,created_at,redeemed_at,events(id,name,starts_at,venue),drinks(name,description)")
        .eq("id", voucherId)
        .maybeSingle();
      setVoucher((data as unknown as VoucherDetail) ?? null);
      setPageLoading(false);
    })();
  }, [voucherId, user]);

  useEffect(() => {
    if (!voucher || voucher.redeemed_at) return;
    void QRCode.toDataURL(voucher.code, {
      errorCorrectionLevel: "H",
      margin: 1,
      width: 480,
      color: { dark: "#000000", light: "#ffffff" },
    }).then(setQrDataUrl);
  }, [voucher]);

  const handleTransfer = async () => {
    if (!voucher) return;
    const email = transferEmail.trim();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error(t("account.emailInvalid"));
      return;
    }
    setTransferring(true);
    const { data, error } = await supabase.rpc("transfer_drink_voucher", {
      _voucher_id: voucher.id,
      _recipient_email: email,
    });
    setTransferring(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const recipientName = (data as { recipient_name?: string } | null)?.recipient_name;
    toast.success(
      recipientName
        ? t("account.voucherDetail.transferSuccessNamed", { name: recipientName })
        : t("account.voucherDetail.transferSuccess"),
    );
    setTransferOpen(false);
    void navigate({ to: "/vouchers" });
  };

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("account.voucherDetail.notFound")}</h1>
          <Button asChild className="mt-4">
            <Link to="/vouchers">{t("account.voucherDetail.backToVouchers")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const fullName = `${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`.trim() || (user.email ?? "");
  const isUsed = !!voucher.redeemed_at;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="mb-4 flex items-center justify-between">
          <Link to="/vouchers" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t("account.vouchers.myVouchers")}
          </Link>
          {!isUsed && (
            <Button variant="outline" size="sm" onClick={() => setTransferOpen(true)}>
              <Send className="mr-1.5 h-4 w-4" /> {t("account.voucherDetail.sendToFriend")}
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-3xl border border-border bg-white text-black shadow-card">
          <div className="flex flex-col items-center p-6 md:p-8">
            <JuenWordmark className="h-7 text-black" />
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-black/60">
              <Wine className="h-3.5 w-3.5" /> {t("account.voucherDetail.badge")}
            </div>

            {voucher.drinks && (
              <h1 className="mt-4 text-center text-2xl font-extrabold leading-tight md:text-3xl">
                {tc(voucher.drinks.name)}
              </h1>
            )}

            <div className="mt-5 rounded-md bg-white p-2">
              {isUsed ? (
                <div className="flex h-56 w-56 flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-black/20 bg-black/5 text-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                  <div className="text-sm font-semibold text-black">{t("account.vouchers.used")}</div>
                  <div className="px-2 text-xs text-black/60">
                    {new Date(voucher.redeemed_at!).toLocaleString(locale)}
                  </div>
                </div>
              ) : qrDataUrl ? (
                <img src={qrDataUrl} alt={`QR: ${voucher.code}`} className="h-56 w-56" />
              ) : (
                <div className="h-56 w-56 animate-pulse bg-black/5" />
              )}
            </div>

            <div className="mt-3 font-mono text-[11px] tracking-wider text-black/70">{voucher.code}</div>

            <div className="mt-4 text-center">
              <div className="text-base font-bold">{fullName}</div>
              <div className="text-xs text-black/60">{user.email}</div>
            </div>

            {voucher.events && (
              <div className="mt-5 w-full rounded-lg border border-black/15 p-4 text-sm">
                <div className="font-semibold">{tc(voucher.events.name)}</div>
                <div className="mt-2 space-y-1.5 text-black/70">
                  <div className="inline-flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    {new Date(voucher.events.starts_at).toLocaleString(locale, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </div>
                  {voucher.events.venue && (
                    <div className="flex items-start gap-1.5">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{tc(voucher.events.venue)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-black/10 px-6 py-4 md:px-8">
            <JuenWordmark className="h-5 text-black" />
            <p className="text-right text-[11px] text-black/60">
              {t("account.voucherDetail.footerLine1")}
              <br />
              {t("account.voucherDetail.footerLine2")}
            </p>
          </div>
        </div>
      </div>

      <AlertDialog open={transferOpen} onOpenChange={setTransferOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("account.voucherDetail.transferTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("account.voucherDetail.transferDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            type="email"
            placeholder={t("account.voucherDetail.emailPlaceholder")}
            value={transferEmail}
            onChange={(e) => setTransferEmail(e.target.value)}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={transferring}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleTransfer} disabled={transferring}>
              {transferring ? t("account.voucherDetail.sending") : t("account.voucherDetail.transferConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
